/**
 * Shared table + frozen-column tokens.
 *
 * Kept in one place so every data table in the app scrolls, pins and rules the
 * same way. Written as plain class strings because Tailwind's JIT scans source
 * text — a class assembled at runtime from fragments would never be generated.
 */

/** Width reserved for a leading checkbox column, in px. */
export const STICKY_CHECKBOX_W = 48;

/**
 * border-separate, not border-collapse.
 *
 * Collapsed borders belong to the table rather than the cell, so they detach
 * from a cell that is pinned with position:sticky. With border-separate the
 * row rules must live on the cells (a <tr> border does not paint) — that is
 * what ROW_RULE / HEADER_RULE are for.
 */
export const TABLE = 'w-full text-left border-separate';
export const TABLE_STYLE = { borderSpacing: 0 };

/** The scroll area. Bounded height is required for a sticky header to have
 *  anything to stick to. */
export const TABLE_SCROLL = 'h-full overflow-auto';

export const HEADER_RULE = 'border-b border-slate-100';
export const ROW_RULE = 'border-b border-slate-50';

export const TH =
  'px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap';
export const TD = 'px-4 py-3 align-top';

/**
 * Stacking order for a table that pins both a column and the header.
 * Pinning the two axes competes, so each layer needs its own level:
 *   plain body cell    auto — scrolls under everything
 *   pinned body column  20  — covers plain cells on horizontal scroll
 *   plain header cell   30  — covers rows AND pinned columns on vertical scroll
 *   pinned header cell  40  — covers plain header cells on horizontal scroll
 */
export const Z_BODY_PINNED = 'z-20';
export const Z_HEADER = 'z-30';
export const Z_HEADER_PINNED = 'z-40';

/** Hairline separators drawn with box-shadow so they cost no layout width. */
export const STICKY_LEFT_EDGE = 'shadow-[1px_0_0_0_rgb(241_245_249)]';
export const STICKY_RIGHT_EDGE = 'shadow-[-1px_0_0_0_rgb(241_245_249)]';

/**
 * Background for a pinned cell.
 *
 * Pinned cells MUST be opaque: the scrolling columns pass underneath them, and
 * a transparent or alpha background lets that content show through. The row's
 * hover/selected tint is therefore applied here as a solid colour rather than
 * the alpha wash a normal row can use.
 */
export const stickyBg = (isSelected) =>
  isSelected ? 'bg-blue-50' : 'bg-white group-hover:bg-slate-50';

/** Row class that makes group-hover work on its pinned cells. */
export const rowClass = (isSelected) =>
  `group transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'}`;
